// SearchResultsPage — POST /searches + SSE stream.
// Shows aggregator status chips, skeleton rows while searching, real rows from SSE.
// On ADAPTER_001 terminal failure → partial-failure banner (per plan §copy).

import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { openSearchStream, type SseAggregatorStatus, type SseSearchStatus, type SseRawEvent } from '@/lib/eventSource';
import { AggregatorStatusChip, type AggregatorState } from '@/components/AggregatorStatusChip';
import { FlightCard, FlightCardSkeleton, type FlightOffer } from '@/components/FlightCard';
import { ErrorState, notYetWiredToast } from '@/components/primitives';
import { useIsMobile } from '@/hooks/useIsMobile';

// ADAPTER_001 user-facing copy (from error-codes.md)
const ADAPTER_001_MESSAGE =
  'Live search is being set up — flight results will be available shortly.';

interface SearchResponse {
  data?: {
    search?: {
      id: string;
      origin: string;
      destination: string;
      departDate: string;
      adults: number;
      status: string;
    };
  };
}

const T = {
  bg: '#0A0F1C', surface: '#131C30', surface2: '#1A2540',
  ink: '#F4F3F0', inkDim: '#8C98B0', primary: '#E8441A', accent: '#4B8EDB',
  yellow: '#F5A623', line: 'rgba(255,255,255,0.07)', lineHi: 'rgba(255,255,255,0.14)', bg2: '#0F1628',
};

const SORT_TABS: Array<[string, string]> = [
  ['Best', '$489'], ['Cheapest', '$398'], ['Fastest', '8h 25m'], ['🌿 Eco', '0.42T'],
];

function FilterGroup({ title, children, last }: { title: string; children: React.ReactNode; last?: boolean }) {
  return (
    <div style={{ borderBottom: last ? 'none' : `1px solid ${T.line}`, paddingBottom: 16, marginBottom: 16 }}>
      <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 11, fontWeight: 700, color: T.inkDim, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

export function SearchResultsPage() {
  const isMobile = useIsMobile();
  const { searchId } = useParams<{ searchId: string }>();
  const navigate = useNavigate();

  const [aggregators, setAggregators] = useState<Record<string, AggregatorState>>({});
  const [offers, setOffers] = useState<FlightOffer[]>([]);
  const [streamStatus, setStreamStatus] = useState<'open' | 'terminal'>('open');
  const [hasAdapter001, setHasAdapter001] = useState(false);
  const [activeSort, setActiveSort] = useState(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Fetch search metadata (for the header breadcrumb)
  const { data: searchData } = useQuery({
    queryKey: ['search', searchId],
    queryFn: async () => {
      if (!searchId) return null;
      const res = await api.get<SearchResponse>(`/searches/${searchId}`);
      return res.data.data?.search ?? null;
    },
    enabled: Boolean(searchId),
  });

  useEffect(() => {
    if (!searchId) return;

    const cleanup = openSearchStream(
      searchId,
      (event: SseRawEvent) => {
        if (event.type === 'aggregator_status') {
          const d = event.data as SseAggregatorStatus;
          setAggregators(prev => ({
            ...prev,
            [d.aggregator]: {
              aggregator: d.aggregator,
              status: d.status,
              ...(d.errorCode !== undefined ? { errorCode: d.errorCode } : {}),
            },
          }));
          if (d.errorCode === 'ADAPTER_001') {
            setHasAdapter001(true);
          }
        } else if (event.type === 'offer') {
          setOffers(prev => [...prev, event.data as FlightOffer]);
        } else if (event.type === 'search_status') {
          const d = event.data as SseSearchStatus;
          const terminal = ['complete', 'failed', 'partial_complete'];
          if (terminal.includes(d.status)) {
            setStreamStatus('terminal');
          }
        }
      },
      () => setStreamStatus('terminal'),
    );

    cleanupRef.current = cleanup;
    return () => cleanup();
  }, [searchId]);

  if (!searchId) {
    return <ErrorState title="Invalid search" body="No search ID found." onRetry={() => navigate('/')}/>;
  }

  const search = searchData;
  const isSearching = streamStatus === 'open';
  const aggList = Object.values(aggregators);

  return (
    <div style={{ background: T.bg, minHeight: '100vh' }}>
      <div style={{ padding: '24px clamp(16px, 4vw, 64px) 80px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 11, letterSpacing: 2, color: T.inkDim, fontWeight: 600 }}>
              {search ? `${search.origin} → ${search.destination} · ${search.departDate} · ${search.adults} ADULT` : 'Loading…'}
            </div>
            <h2 style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 'clamp(28px,4vw,48px)', fontWeight: 600, color: T.ink, margin: '6px 0 0', letterSpacing: -0.02, lineHeight: 1 }}>
              {isSearching ? 'Searching…' : `${offers.length} flight${offers.length !== 1 ? 's' : ''} found.`}
            </h2>
          </div>
          {/* Sort tabs */}
          <div style={{ display: 'flex', gap: 8 }}>
            {SORT_TABS.map(([l, s], i) => (
              <button key={l} onClick={() => setActiveSort(i)} style={{
                padding: '10px 16px', borderRadius: 14,
                background: activeSort === i ? T.ink : T.surface,
                color: activeSort === i ? T.bg : T.ink,
                border: activeSort === i ? 'none' : `1px solid ${T.line}`,
                fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                textAlign: 'left', minWidth: 100,
              }}>
                <div>{l}</div>
                <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 500, marginTop: 2 }}>{s}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Aggregator status chips */}
        {aggList.length > 0 && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            {aggList.map(a => <AggregatorStatusChip key={a.aggregator} state={a}/>)}
          </div>
        )}
        {aggList.length === 0 && isSearching && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            {['TBO', 'Mystifly'].map(agg => (
              <AggregatorStatusChip key={agg} state={{ aggregator: agg, status: 'pending' }}/>
            ))}
          </div>
        )}

        {/* ADAPTER_001 partial-failure banner */}
        {hasAdapter001 && (
          <div style={{
            padding: '14px 20px', borderRadius: 14, marginBottom: 16,
            background: 'rgba(232,68,26,0.08)', border: '1px solid rgba(232,68,26,0.2)',
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <span style={{ fontSize: 20 }}>⚠</span>
            <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 14, color: T.ink }}>
              {ADAPTER_001_MESSAGE}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '280px 1fr', gap: 24 }}>
          {/* Sidebar filters */}
          <div style={{
            background: T.surface, borderRadius: 22, padding: 22,
            border: `1px solid ${T.line}`, alignSelf: 'start',
            position: 'sticky', top: 88,
          }}>
            <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 18 }}>Filters</div>

            <FilterGroup title="Stops">
              {[['Direct only', '$421'], ['1 stop', '$398'], ['2+ stops', '$348']].map(([l, p], i) => (
                <label key={l} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
                  <input type="checkbox" defaultChecked={i < 2} style={{ accentColor: T.primary, width: 16, height: 16 }}/>
                  <span style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 13, color: T.ink, flex: 1 }}>{l}</span>
                  <span style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 11, color: T.inkDim }}>{p}</span>
                </label>
              ))}
            </FilterGroup>

            <FilterGroup title="Departure">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6 }}>
                {[['Early', '00–06'], ['Morning', '06–12'], ['Afternoon', '12–18'], ['Evening', '18–24']].map(([l, t], i) => (
                  <button key={l} style={{
                    padding: '8px 10px', borderRadius: 10,
                    background: i === 1 ? T.surface2 : 'transparent',
                    border: `1px solid ${i === 1 ? T.lineHi : T.line}`,
                    cursor: 'pointer', textAlign: 'left',
                  }}>
                    <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 12, fontWeight: 600, color: T.ink }}>{l}</div>
                    <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 10, color: T.inkDim, marginTop: 2 }}>{t}</div>
                  </button>
                ))}
              </div>
            </FilterGroup>

            <FilterGroup title="Airlines">
              {['Singapore Air', 'AirAsia', 'IndiGo', 'Qatar', 'Emirates'].map((l, i) => (
                <label key={l} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
                  <input type="checkbox" defaultChecked={i < 3} style={{ accentColor: T.primary, width: 16, height: 16 }}/>
                  <span style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 13, color: T.ink }}>{l}</span>
                </label>
              ))}
            </FilterGroup>

            <FilterGroup title="Carbon" last>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
                <input type="checkbox" defaultChecked style={{ accentColor: T.primary, width: 16, height: 16 }}/>
                <span style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 13, color: T.ink }}>🌿 Lower CO₂ only</span>
              </label>
            </FilterGroup>
          </div>

          {/* Results */}
          <div>
            {/* AI insight */}
            <div style={{
              padding: '16px 20px', borderRadius: 18, marginBottom: 14,
              background: `linear-gradient(135deg, rgba(232,68,26,0.07), rgba(75,142,219,0.07))`,
              border: `1px solid rgba(232,68,26,0.2)`,
              display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 12, background: 'linear-gradient(135deg, #E8441A, #4B8EDB)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 16, color: T.bg }}>✦</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 14, fontWeight: 600, color: T.ink }}>Prices likely to drop ~8% in 3 days</div>
                <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 12, color: T.inkDim, marginTop: 2 }}>Based on historical trends · We'll alert you when it does</div>
              </div>
              <button onClick={() => notYetWiredToast('Price Alerts')} style={{ padding: '8px 14px', borderRadius: 999, background: T.surface, color: T.ink, border: `1px solid ${T.line}`, fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Track price</button>
            </div>

            {/* Flight rows */}
            {offers.length > 0
              ? offers.map(f => <FlightCard key={f.id} offer={f}/>)
              : isSearching
              ? Array.from({ length: 4 }).map((_, i) => <FlightCardSkeleton key={i}/>)
              : (
                <div style={{ padding: '40px 0', textAlign: 'center', color: T.inkDim }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>✈</div>
                  <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 16, fontWeight: 600, color: T.ink }}>No flights found</div>
                  <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 13, marginTop: 6 }}>
                    {hasAdapter001
                      ? 'Live adapter setup in progress — check back shortly.'
                      : 'Try adjusting your dates or filters.'}
                  </div>
                </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
}
